// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a short banner under the account chip about the licence in use
 * (several licences on one account, `docs/AYNI_XR_MULTIPLE_LICENCES_PLAN.md`):
 * why it changed by itself (decision M6), or what closed when a switch left
 * something outside the new plan (M1). One at a time, with **Your plan**
 * and **OK**. The text is set as text, never as HTML.
 */

import { t } from '../i18n'

export interface LicenceBannerOptions {
  text: string
  /** Shows **Your plan**, which closes the banner and calls this. */
  onPlan?: (trigger: HTMLElement) => void
  /** Called when the banner is closed with **OK** or by **Your plan**. */
  onDismiss?: () => void
}

let current: HTMLElement | null = null

export function hideLicenceBanner(): void {
  current?.remove()
  current = null
}

export function showLicenceBanner(opts: LicenceBannerOptions): void {
  hideLicenceBanner()
  const box = document.createElement('div')
  box.id = 'licence-banner'
  box.className = 'licence-banner'
  box.setAttribute('role', 'status')
  const text = document.createElement('p')
  text.className = 'licence-banner-text'
  text.textContent = opts.text
  const actions = document.createElement('div')
  actions.className = 'licence-banner-actions'
  const close = (): void => {
    hideLicenceBanner()
    opts.onDismiss?.()
  }
  if (opts.onPlan) {
    const plan = document.createElement('button')
    plan.type = 'button'
    plan.className = 'licence-banner-plan'
    plan.textContent = t('account.banner.yourPlan')
    plan.addEventListener('click', () => {
      close()
      opts.onPlan?.(document.getElementById('account-menu-button') ?? plan)
    })
    actions.append(plan)
  }
  const ok = document.createElement('button')
  ok.type = 'button'
  ok.className = 'licence-banner-ok'
  ok.textContent = t('account.banner.ok')
  ok.addEventListener('click', close)
  actions.append(ok)
  box.append(text, actions)
  document.body.append(box)
  current = box
}
